import {
  isDescriptorControlField,
  isPropertyValue,
  resolvePropertyValue,
} from './dood-block-config';
import {
  applyComposeAliases,
  collectComposeAliasesFromProperties,
} from './compose-alias';
import type { BlockContext } from './property-value';

export type ResolveBlockConfigInput<Config extends Record<string, unknown>> = {
  legacyConfig: Partial<Config>;
  overrideConfig: Partial<Config>;
  config: Partial<Config>;
  defaultConfig: Config;
  context: BlockContext;
};

/** Runtime input for block resolvers created by `createBlockConfigResolver`. */
export type ResolveBlockConfigRuntimeInput<Config extends Record<string, unknown>> = {
  legacyConfig?: Partial<Config>;
  config?: Partial<Config>;
  context: BlockContext;
};

function isPlainMergeObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  if ('type' in value && 'value' in value) {
    return false;
  }
  return true;
}

/**
 * Deep-merges partial config layers. Later layers win; `undefined` leaves the
 * previous value unchanged.
 */
export function deepMergeConfigLayers<Config extends Record<string, unknown>>(
  ...layers: Partial<Config>[]
): Config {
  const result: Record<string, unknown> = {};

  for (const layer of layers) {
    if (!layer) {
      continue;
    }
    for (const [key, value] of Object.entries(layer as Record<string, unknown>)) {
      if (value === undefined) {
        continue;
      }
      const existing = result[key];
      if (isPlainMergeObject(value) && isPlainMergeObject(existing)) {
        result[key] = deepMergeConfigLayers(existing, value);
      } else {
        result[key] = value;
      }
    }
  }

  return result as Config;
}

function applyConditionalPropertyResolution(
  propertiesNode: unknown,
  configNode: unknown,
  context: BlockContext,
  out: Record<string, unknown>,
): void {
  if (!propertiesNode || typeof propertiesNode !== 'object') {
    return;
  }

  for (const [key, propertyChild] of Object.entries(
    propertiesNode as Record<string, unknown>,
  )) {
    if (isPropertyValue(propertyChild)) {
      if (propertyChild.kind === 'conditional' && propertyChild.conditions?.length) {
        const resolved = resolvePropertyValue(propertyChild, context);
        if (resolved !== undefined) {
          out[key] = resolved;
        }
      }
      continue;
    }

    if (isDescriptorControlField(propertyChild)) {
      continue;
    }

    if (propertyChild && typeof propertyChild === 'object') {
      const nestedConfig =
        configNode && typeof configNode === 'object'
          ? (configNode as Record<string, unknown>)[key]
          : undefined;
      const nestedOut =
        out[key] && typeof out[key] === 'object'
          ? (out[key] as Record<string, unknown>)
          : {};
      applyConditionalPropertyResolution(
        propertyChild,
        nestedConfig,
        context,
        nestedOut,
      );
      out[key] = nestedOut;
    }
  }
}

/**
 * Resolves a block config from default, legacy, parent override and CB2 inputs.
 * Priority (lowest to highest): defaultConfig → legacyConfig → overrideConfig → config.
 */
export function resolveBlockConfig<Config extends Record<string, unknown>>(
  definition: {
    properties: unknown;
    config: Config;
    configToOverride: Partial<Config>;
  },
  input: ResolveBlockConfigInput<Config>,
): Config {
  const merged = deepMergeConfigLayers<Config>(
    input.defaultConfig,
    input.legacyConfig,
    input.overrideConfig,
    input.config,
  );

  const resolved = { ...(merged as Record<string, unknown>) };
  applyConditionalPropertyResolution(
    definition.properties,
    merged,
    input.context,
    resolved,
  );

  const composeAliases = collectComposeAliasesFromProperties(definition.properties);
  if (Object.keys(composeAliases).length > 0) {
    return applyComposeAliases(resolved as Config, composeAliases);
  }

  return resolved as Config;
}

export function createBlockConfigResolver<Config extends Record<string, unknown>>(
  definition: {
    properties: unknown;
    config: Config;
    configToOverride: Partial<Config>;
  },
): (input: ResolveBlockConfigRuntimeInput<Config>) => Config {
  return input =>
    resolveBlockConfig(definition, {
      defaultConfig: definition.config,
      overrideConfig: definition.configToOverride,
      legacyConfig: input.legacyConfig ?? {},
      config: input.config ?? {},
      context: input.context,
    });
}
